"use client";
import { useState, useEffect, ChangeEvent, FormEvent } from "react";
import { User, Edit2 } from "lucide-react";
import api from "../../services/api";
import { useToast } from "../../hooks/useToast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import DateCalendar from "../DateCalendar";
import { Calendar as CalendarIcon } from "lucide-react";

type CustomerForm = {
  name: string;
  email: string;
  mobile: string;
  date_of_birth: string;
  image: File | null;
  calling_code: string;
  landline: string;
  password?: string;
};

interface AddCustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  editData?: any;
}

export default function AddCustomerModal({
  isOpen,
  onClose,
  onSave,
  editData,
}: AddCustomerModalProps) {
  const toast = useToast();
  const [form, setForm] = useState<CustomerForm>({
    name: "",
    email: "",
    mobile: "",
    date_of_birth: "",
    image: null,
    calling_code: "+44",
    landline: "",
    password: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isDateOpen, setIsDateOpen] = useState(false);

  useEffect(() => {
    if (editData) {
      setForm({
        name: editData.name || "",
        email: editData.email || "",
        mobile: editData.mobile || "",
        date_of_birth: editData.date_of_birth || "",
        image: null,
        calling_code: editData.calling_code || "+44",
        landline: editData.landline || "",
        password: "",
      });
      if (editData.user_image_url) {
        setImagePreview(editData.user_image_url);
      }
    } else {
      // Reset form when modal opens for adding new customer
      setForm({
        name: "",
        email: "",
        mobile: "",
        date_of_birth: "",
        image: null,
        calling_code: "+44",
        landline: "",
        password: "",
      });
      setImagePreview(null);
    }
    setError(null);
    setFieldErrors({});
  }, [editData, isOpen]);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    // Clear field-specific error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    if (error) setError(null);
  };

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setForm((prev) => ({ ...prev, image: file }));

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string[]> = {};

    if (!form.name.trim()) {
      newErrors.name = ["Client name is required"];
    } else if (form.name.trim().length < 2) {
      newErrors.name = ["Name must be at least 2 characters long"];
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!form.email.trim()) {
      newErrors.email = ["Email address is required"];
    } else if (!emailRegex.test(form.email)) {
      newErrors.email = ["Please enter a valid email address (e.g., name@example.com)"];
    }

    if (!form.mobile.trim()) {
      newErrors.mobile = ["Mobile number is required"];
    } else if (form.mobile.trim().length < 7) {
      newErrors.mobile = ["Mobile number is too short"];
    }

    if (!editData && !form.password) {
      newErrors.password = ["Password is required for new clients"];
    } else if (form.password && form.password.length < 6) {
      newErrors.password = ["Password must be at least 6 characters long"];
    }

    setFieldErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    setError(null);
    setFieldErrors({});

    if (!validateForm()) {
      toast.error("Please correct the errors in the form");
      return;
    }

    setLoading(true);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append("name", form.name);
      formData.append("email", form.email);
      formData.append("mobile", form.mobile);
      formData.append("date_of_birth", form.date_of_birth);
      formData.append("calling_code", form.calling_code);
      formData.append("landline", form.landline);


      if (form.image) {
        formData.append("image", form.image);
      }

      let response;

      if (editData) {
        // Update existing customer
        formData.append("_method", 'PUT');
        response = await api.post(`/admin/customers/${editData.id}`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      } else {
        // Create new customer
        response = await api.post("/admin/customers", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      }

      if (response.data.success) {
        toast.success(editData ? "Customer updated successfully" : "Customer added successfully");
        onSave(response.data.data);
        onClose();
        // Reset form
        setForm({
          name: "",
          email: "",
          mobile: "",
          date_of_birth: "",
          image: null,
          calling_code: "+44",
          landline: "",
          password: "",
        });
        setImagePreview(null);
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Failed to save customer. Please try again.";
      setError(errorMessage);

      const errors = err.response?.data?.errors;
      if (errors) {
        setFieldErrors(errors);
        // Toast the first error message for immediate feedback
        const firstErrorKey = Object.keys(errors)[0];
        if (firstErrorKey && errors[firstErrorKey][0]) {
          toast.error(errors[firstErrorKey][0]);
        } else {
          toast.error(errorMessage);
        }
      } else {
        toast.error(errorMessage);
      }

      console.error("Error saving customer:", err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-white z-[1000] flex flex-col">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center border-b px-6 md:px-12 py-5 md:py-6 gap-4">
        <h2 className="text-xl md:text-2xl font-bold text-gray-900">
          {editData ? "Edit client" : "Add a new client"}
        </h2>
        <div className="flex gap-3 md:gap-4">
          <button
            onClick={onClose}
            type="button"
            disabled={loading}
            className="px-4 md:px-5 py-2 border border-gray-300 rounded-lg text-sm md:text-base font-medium hover:bg-gray-50 disabled:opacity-50"
          >
            Close
          </button>
          <button
            type="submit"
            form="client-form"
            disabled={loading}
            className="px-4 md:px-5 py-2 bg-[#885ABB] text-white rounded-lg text-sm md:text-base font-medium hover:bg-[#6f47a2] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 overflow-hidden flex-col md:flex-row">
        {/* Sidebar */}
        <aside className="w-full md:w-64 border-b md:border-r p-4 md:p-6 text-sm md:text-base">
          <nav className="space-y-4 md:space-y-6">
            <div>
              <p className="font-semibold mb-2 md:mb-3 text-gray-900">Personal</p>
              <ul className="flex md:flex-col gap-2 md:gap-0">
                <li className="bg-purple-50 text-[#885ABB] border border-purple-200 rounded-lg px-3 py-2 font-medium text-sm">
                  Profile
                </li>
              </ul>
            </div>
          </nav>
        </aside>

        {/* Content */}
        <main className="flex-1 p-6 md:p-10 overflow-y-auto">
          <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
            Profile
          </h3>
          <p className="text-gray-600 text-sm md:text-base mb-6 md:mb-8">
            Manage your client&apos;s personal profile
          </p>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Avatar */}
          <div className="flex items-center gap-4 md:gap-5 mb-6 md:mb-8">
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-purple-100 flex items-center justify-center relative border border-purple-200 overflow-hidden">
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt="Customer"
                  className="w-full h-full object-cover"
                />
              ) : editData?.user_image_url ? (
                <img
                  src={editData.user_image_url}
                  alt="Customer"
                  className="w-full h-full object-cover"
                />
              ) : (
                <User className="w-10 h-10 md:w-12 md:h-12 text-purple-700" />
              )}
              <label
                htmlFor="image-upload"
                className="absolute bottom-0 right-0 bg-white border border-gray-300 rounded-full p-1.5 shadow-sm hover:bg-gray-50 cursor-pointer"
              >
                <Edit2 className="w-3 h-3 md:h-4 text-gray-700" />
                <input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          {/* Form */}
          <form
            id="client-form"
            onSubmit={handleSubmit}
            className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 max-w-full md:max-w-5xl"
          >
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="e.g. John Doe"
                className={`w-full border rounded-lg p-2.5 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${fieldErrors.name ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-purple-300'}`}
                required
              />
              {fieldErrors.name && (
                <p className="text-red-500 text-xs mt-1 font-medium flex items-center gap-1">
                  <span className="w-1 h-1 bg-red-500 rounded-full" /> {fieldErrors.name[0]}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                placeholder="example@domain.com"
                className={`w-full border rounded-lg p-2.5 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${fieldErrors.email ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-purple-300'}`}
                required
              />
              {fieldErrors.email && (
                <p className="text-red-500 text-xs mt-1 font-medium flex items-center gap-1">
                  <span className="w-1 h-1 bg-red-500 rounded-full" /> {fieldErrors.email[0]}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Mobile <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <Select
                  value={form.calling_code}
                  onValueChange={(value) => setForm(prev => ({ ...prev, calling_code: value }))}
                >
                  <SelectTrigger className="w-[100px] border border-gray-300 rounded-lg h-[45px] focus:ring-purple-500">
                    <SelectValue placeholder="+44" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200 shadow-xl rounded-xl z-[1100]">
                    <SelectItem value="+44">+44</SelectItem>
                    <SelectItem value="+1">+1</SelectItem>
                    <SelectItem value="+353">+3</SelectItem>
                    <SelectItem value="+61">+61</SelectItem>
                    <SelectItem value="+91">+9</SelectItem>
                    <SelectItem value="+92">+92</SelectItem>
                  </SelectContent>
                </Select>
                <input
                  name="mobile"
                  value={form.mobile}
                  onChange={handleChange}
                  placeholder="e.g. 7123 456789"
                  className={`flex-1 border rounded-lg p-2.5 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${fieldErrors.mobile ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-purple-300'}`}
                  required
                />
              </div>
              {fieldErrors.mobile && (
                <p className="text-red-500 text-xs mt-1 font-medium flex items-center gap-1">
                  <span className="w-1 h-1 bg-red-500 rounded-full" /> {fieldErrors.mobile[0]}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Date of Birth
              </label>
              <Popover open={isDateOpen} onOpenChange={setIsDateOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className={`w-full flex items-center justify-between border rounded-lg p-2.5 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${fieldErrors.date_of_birth ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-purple-300'}`}
                  >
                    <span className={form.date_of_birth ? "text-gray-900" : "text-gray-400"}>
                      {form.date_of_birth && !isNaN(new Date(form.date_of_birth).getTime())
                        ? new Date(form.date_of_birth).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                        : "Select date"}
                    </span>
                    <CalendarIcon className="w-4 h-4 text-[#885ABB]" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 z-[1100] bg-white border border-gray-200 shadow-xl rounded-xl">
                  <DateCalendar
                    selectedDate={form.date_of_birth && !isNaN(new Date(form.date_of_birth).getTime()) ? new Date(form.date_of_birth) : new Date()}
                    onSelectDate={(date) => {
                      setForm(prev => ({ ...prev, date_of_birth: date.toISOString().split('T')[0] }));
                      setIsDateOpen(false);
                    }}
                  />
                </PopoverContent>
              </Popover>
              {fieldErrors.date_of_birth && (
                <p className="text-red-500 text-xs mt-1 font-medium flex items-center gap-1">
                  <span className="w-1 h-1 bg-red-500 rounded-full" /> {fieldErrors.date_of_birth[0]}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Landline
              </label>
              <input
                name="landline"
                value={form.landline}
                onChange={handleChange}
                placeholder="231231231"
                className={`w-full border rounded-lg p-2.5 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${fieldErrors.landline ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-purple-300'}`}
              />
              {fieldErrors.landline && (
                <p className="text-red-500 text-xs mt-1 font-medium flex items-center gap-1">
                  <span className="w-1 h-1 bg-red-500 rounded-full" /> {fieldErrors.landline[0]}
                </p>
              )}
            </div>

            <div className="">
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Password {!editData && <span className="text-red-500">*</span>}
                {editData && <span className="text-gray-500 text-xs ml-2">(Leave blank to keep current password)</span>}
              </label>
              <input
                name="password"
                type="password"
                value={form.password}
                onChange={handleChange}
                placeholder={editData ? "Enter new password (optional)" : "Enter password"}
                className={`w-full border rounded-lg p-2.5 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${fieldErrors.password ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-purple-300'}`}
                required={!editData}
              />
              {fieldErrors.password && (
                <p className="text-red-500 text-xs mt-1 font-medium flex items-center gap-1">
                  <span className="w-1 h-1 bg-red-500 rounded-full" /> {fieldErrors.password[0]}
                </p>
              )}
            </div>
          </form>
        </main>
      </div>
    </div>
  );
}
